import { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { autoRegApi, type AutoRegistrationRuleResponse } from '@/api/endpoints/autoRegistration'
import Spinner from '@/components/common/Spinner'
import { classesApi, type ClassResponse } from '@/api/endpoints/classes'
import { useUI } from '@/stores/ui'

interface ClassOption { id: string; name: string }

export default function AutoRegistrationRules() {
  const addToast = useUI((state) => state.addToast)
  const [rules, setRules] = useState<AutoRegistrationRuleResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [minAge, setMinAge] = useState<number>(5)
  const [maxAge, setMaxAge] = useState<number>(7)
  const [classId, setClassId] = useState<string | 'NO_REG'>('NO_REG')
  const [excludeNonParish, setExcludeNonParish] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [showNoClassError, setShowNoClassError] = useState(false)

  const [classes, setClasses] = useState<ClassOption[]>([])

  // Load classes and rules
  const loadData = async () => {
    setLoading(true)
    try {
      // Load classes separately so CORS error on auto-registration doesn't block it
      try {
        const rulesData = await autoRegApi.list()
        setRules(Array.isArray(rulesData) ? rulesData : [])
      } catch (error: any) {
        console.error('Auto-Registration Rules: Failed to load rules', error)
        setRules([])
        // Don't fail completely if rules fail to load
      }
      
      try {
        const classData = await classesApi.list()
        const classOptions: ClassOption[] = (Array.isArray(classData) ? classData : []).map((c: ClassResponse) => ({ id: c.id, name: c.name }))
        setClasses(classOptions)
        console.log('Auto-Registration Rules: Loaded classes', classOptions.length, classOptions)
      } catch (error: any) {
        console.error('Auto-Registration Rules: Failed to load classes', error)
        setClasses([])
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let mounted = true
    loadData()
    
    // Listen for class creation/update events to refresh the dropdown
    const handleClassChange = () => {
      if (mounted) {
        // Refresh classes list when a class is created or updated
        classesApi.list().then(classData => {
          if (mounted) {
            const classOptions: ClassOption[] = (Array.isArray(classData) ? classData : []).map((c: ClassResponse) => ({ id: c.id, name: c.name }))
            setClasses(classOptions)
            console.log('Auto-Registration Rules: Refreshed classes after event', classOptions.length, classOptions)
          }
        }).catch((error) => {
          console.error('Auto-Registration Rules: Failed to refresh classes', error)
        })
      }
    }
    
    // Listen for custom event dispatched when classes change
    window.addEventListener('classes-updated', handleClassChange)
    
    return () => { 
      mounted = false
      window.removeEventListener('classes-updated', handleClassChange)
    }
  }, [])

  const displayRules = useMemo(() => {
    return [...rules].sort((a, b) => a.age_from - b.age_from)
  }, [rules])

  return (
    <motion.div
      className="mt-6 p-5 rounded-xl border border-gray-200 bg-white shadow-sm"
      aria-busy={loading}
      initial={{ y: 8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold">Auto-Registration Rules</h3>
      </div>
      {loading ? (
        <Spinner label="Loading rules…" />
      ) : (
        <div className="space-y-4">
          <form
            className="grid grid-cols-1 sm:grid-cols-5 gap-3"
            onSubmit={async (e) => {
              e.preventDefault()
              const isNoReg = classId === 'NO_REG'
              
              // Validate that a class is selected
              if (isNoReg) {
                setShowNoClassError(true)
                setTimeout(() => setShowNoClassError(false), 5000)
                return
              }
              
              setSubmitting(true)
              setShowNoClassError(false)
              try {
                const newRule = await autoRegApi.create({
                  age_from: minAge,
                  age_to: maxAge,
                  auto_assign_to_class_id: classId,
                  skip_if_not_parishioner: excludeNonParish,
                  is_no_registration: false,
                  priority: 0,
                })
                // Add class name for display
                const ruleWithName = {
                  ...newRule,
                  class_name: classes.find(c => c.id === classId)?.name ?? null,
                }
                setRules(prev => [...prev, ruleWithName])
                setMinAge(5)
                setMaxAge(7)
                setClassId('NO_REG')
              } catch (error: any) {
                console.error('Failed to create rule:', error)
                addToast({
                  message: error.response?.data?.detail || 'Failed to create rule',
                  type: 'error',
                })
              } finally {
                setSubmitting(false)
              }
            }}
          >
            <div>
              <label className="block text-xs font-medium mb-1">From age</label>
              <input 
                type="number" 
                min={0} 
                max={maxAge > 0 ? maxAge - 1 : undefined}
                className="input-field w-full" 
                value={minAge} 
                onChange={(e) => {
                  const val = Number(e.target.value)
                  if (val < maxAge) {
                    setMinAge(val)
                  }
                }} 
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">To age</label>
              <input 
                type="number" 
                min={minAge + 1} 
                className="input-field w-full" 
                value={maxAge} 
                onChange={(e) => {
                  const val = Number(e.target.value)
                  if (val > minAge) {
                    setMaxAge(val)
                  } else {
                    setMaxAge(minAge + 1)
                  }
                }} 
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Assign to</label>
              <select className="input-field w-full" value={classId} onChange={(e) => setClassId(e.target.value)}>
                <option value={'NO_REG'}>SELECT A CLASS</option>
                {classes.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input id="exclude-np" type="checkbox" checked={excludeNonParish} onChange={(e) => setExcludeNonParish(e.target.checked)} />
              <label htmlFor="exclude-np" className="text-sm">Exclude non-parishioners</label>
            </div>
            <div>
              <button className="btn-primary" disabled={submitting}>Add Rule</button>
              {showNoClassError && (
                <p className="text-xs text-red-600 mt-1">Must select a class to auto-assign to</p>
              )}
            </div>
          </form>
          {displayRules.length === 0 ? (
            <div className="text-sm text-gray-500">No rules configured.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {displayRules.map(r => (
                <div key={r.id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium">Age {r.age_from}–{r.age_to}</div>
                    <div className="text-xs text-gray-600">
                      {r.is_no_registration ? 'NO REGISTRATION' : (r.class_name || 'Assigned class')}
                      {r.skip_if_not_parishioner ? ' • exclude non-parishioners' : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="btn-secondary text-xs focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                      onClick={async () => {
                        if (confirm(`Delete rule for age ${r.age_from}–${r.age_to}?`)) {
                          const previous = rules
                          setRules(prev => prev.filter(x => x.id !== r.id))
                          try {
                            await autoRegApi.remove(r.id)
                          } catch {
                            setRules(previous)
                          }
                        }
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  )
}



